import React from 'react';
import { Badge, BlockStack, Box, Card, InlineGrid, InlineStack, Link, Text } from '@shopify/polaris';

const host = (u) => { try { return new URL(u).host; } catch { return String(u); } };
const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function ago(iso) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

const Err = ({ children }) => <Text as="p" variant="bodySm" tone="critical">{children}</Text>;

function DescriptionSection({ description }) {
  return (
    <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
      <BlockStack gap="150">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h4" variant="headingXs" tone="subdued">DESCRIPTION READING</Text>
          {description && description.cached && <Badge>cached</Badge>}
          {description && description.carried && <Badge>earlier run</Badge>}
        </InlineStack>
        {!description ? <Text as="p" variant="bodySm" tone="subdued">not run</Text> : (
          <>
            {description.attributes != null && !description.error && <Text as="p" variant="bodySm" tone="subdued">{description.attributes} attributes of the category asked from the product's own text</Text>}
            {description.error && <Err>{description.error}</Err>}
          </>
        )}
      </BlockStack>
    </Box>
  );
}

function ImageSection({ image }) {
  return (
    <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
      <BlockStack gap="150">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h4" variant="headingXs" tone="subdued">IMAGE READING</Text>
          {image && image.cached && <Badge>cached</Badge>}
          {image && image.carried && <Badge>earlier run</Badge>}
        </InlineStack>
        {!image ? <Text as="p" variant="bodySm" tone="subdued">not run</Text> : (
          <>
            {image.description && <Text as="p" variant="bodySm" breakWord>{image.description}</Text>}
            {image.kind && <Text as="p" variant="bodySm"><Text as="span" tone="subdued">Kind </Text>{image.kind}</Text>}
            {image.error && <Err>{image.error}</Err>}
          </>
        )}
      </BlockStack>
    </Box>
  );
}

function StyleSection({ style }) {
  return (
    <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
      <BlockStack gap="150">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h4" variant="headingXs" tone="subdued">VENDOR PAGE</Text>
          {style && style.key && <Badge>{`searched by name${style.basis === 'article' ? ` and article ${style.key.split(':').slice(1).join(':')}` : ''}`}</Badge>}
          {style && style.cached && <Badge>cached</Badge>}
          {style && style.carried && <Badge>earlier run</Badge>}
        </InlineStack>
        {!style ? <Text as="p" variant="bodySm" tone="subdued">not run</Text> : (
          <>
            {style.vendor_url ? (
              <InlineStack gap="200" blockAlign="center">
                <Link url={style.vendor_url} external>{host(style.vendor_url)}</Link>
                {style.vendor_host_kind === 'vendor' ? <Badge tone="success">brand site</Badge> : <Badge>retailer</Badge>}
              </InlineStack>
            ) : <Text as="p" variant="bodySm" tone="subdued">no page found</Text>}
            {style.official_name && <Text as="p" variant="bodySm" breakWord><Text as="span" tone="subdued">Official name </Text>{style.official_name}</Text>}
            {style.msrp_usd != null && style.msrp_usd !== '' && <Text as="p" variant="bodySm"><Text as="span" tone="subdued">MSRP </Text>{`$${style.msrp_usd}`}</Text>}
            {style.material && <Text as="p" variant="bodySm" breakWord><Text as="span" tone="subdued">Material </Text>{style.material}</Text>}
            {style.error && <Err>{style.error}</Err>}
          </>
        )}
      </BlockStack>
    </Box>
  );
}

function PickSection({ pick }) {
  return (
    <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
      <BlockStack gap="150">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h4" variant="headingXs" tone="subdued">CATEGORY PICK</Text>
          {pick && pick.carried && <Badge>earlier run</Badge>}
        </InlineStack>
        {!pick ? (
          <InlineStack gap="200" blockAlign="center"><Badge>not needed</Badge><Text as="span" variant="bodySm">the rules and the pages already agreed.</Text></InlineStack>
        ) : (
          <>
            {pick.category && (pick.below_floor
              ? <Text as="p" variant="bodySm" tone="subdued" breakWord>{`below floor: ${pick.category}`}</Text>
              : <Text as="p" variant="bodySm" fontWeight="semibold" breakWord>{pick.category}</Text>)}
            {pick.reason && <Text as="p" variant="bodySm" breakWord>{pick.reason}</Text>}
            {pick.confidence != null && <Text as="p" variant="bodySm" tone="subdued">confidence {String(pick.confidence)}</Text>}
            {pick.error && <Err>{pick.error}</Err>}
          </>
        )}
      </BlockStack>
    </Box>
  );
}

export default function AiCard({ ai, costCents }) {
  if (!ai) {
    return (
      <Card>
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">AI analysis</Text>
          <Text as="p">Not analyzed with AI yet</Text>
          <Text as="p" variant="bodySm" tone="subdued">Use Analyze with AI above.</Text>
        </BlockStack>
      </Card>
    );
  }
  if (!ai.description && !ai.image && !ai.style && !ai.category_pick) {   // an auto run that found nothing to ask: say so, or it looks broken
    return (
      <Card>
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">AI analysis</Text>
          <Text as="p">Analyzed {ago(ai.at)} with layers {ai.layers || 'auto'}: nothing was unsettled for this category, so no paid layer ran (no image read, vendor page or category pick, $0.00).</Text>
          <Text as="p" variant="bodySm" tone="subdued">The rules were sure enough about every attribute the AI layers can answer here. To read the image and the vendor page anyway, use Analyze again above (every layer, fresh) or Force AI on the Review tab.</Text>
        </BlockStack>
      </Card>
    );
  }
  const models = ai.models || {};
  const cost = costCents != null ? costCents : [ai.image, ai.style, ai.category_pick].reduce((s, x) => s + ((x && x.cost_cents) || 0), 0);
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" wrap>
          <InlineStack gap="200" blockAlign="center"><Text as="h3" variant="headingSm">AI analysis</Text><Badge tone="info">Claude</Badge></InlineStack>
          <Text as="span" variant="bodySm" tone="subdued">
            {`images: ${models.image || '—'} · pages & category: ${models.lookup || models.category || '—'} · this product cost ${dollars(cost)}${ai.at ? ` · ${ago(ai.at)}` : ''}`}
          </Text>
        </InlineStack>
        <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
          <DescriptionSection description={ai.description} />
          <ImageSection image={ai.image} />
          <StyleSection style={ai.style} />
          <PickSection pick={ai.category_pick} />
        </InlineGrid>
      </BlockStack>
    </Card>
  );
}
